import { automationSessionSelectByQuery, botDeploymentSelectOneObjectByQuery, botSelectByQuery } from '@common-reducers/DBServiceThunks';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { memo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

interface DeploymentCardProps {
    deploymentId: string;
    isSelected: boolean;
    onSelect: () => void;
}

export const DeploymentCard = memo(({ deploymentId, isSelected, onSelect }: DeploymentCardProps) => {
    const { t } = useTranslation(namespace);
    const deployment = useAppSelector(state => 
        botDeploymentSelectOneObjectByQuery(state.DBBotDeploymentReducer, { _id: deploymentId })
    );
    
    const sessionCount = useAppSelector(state =>
        (automationSessionSelectByQuery(state.DBAutomationSessionReducer, {
            botDeploymentId: deploymentId
        }) ?? []).length
    );
    
    const activeSessions = useAppSelector(state =>
        (automationSessionSelectByQuery(state.DBAutomationSessionReducer, {
            botDeploymentId: deploymentId,
            status: { $in: ['open', 'processing'] }
        }) ?? []).length
    );
    
    const isActive = useAppSelector(state => {
        const bots = botSelectByQuery(state.DBBotReducer, {}) || [];
        const bot = bots.find(b => b.activeDeploymentId === deploymentId);
        return !!bot;
    });

    if (!deployment) return null;

    const deploymentDate = deployment.createdAt 
        ? formatDistanceToNow(new Date(deployment.createdAt), { addSuffix: true })
        : t('chatbot.botDetail.deployments.unknownDate');

    return (
        <div 
            style={{
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid var(--deployments-sidebar-item-border)',
                cursor: 'pointer',
                transition: 'background-color 200ms',
                backgroundColor: isSelected 
                    ? 'var(--deployments-sidebar-item-selected-bg)' 
                    : 'var(--deployments-sidebar-item-bg)',
            }}
            onClick={onSelect}
            onMouseEnter={(e) => {
                if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'var(--deployments-sidebar-item-hover-bg)';
                }
            }}
            onMouseLeave={(e) => {
                if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'var(--deployments-sidebar-item-bg)';
                }
            }}
        >
            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                    <span style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: 'var(--deployments-sidebar-version-text)',
                    }}>
                        {t('chatbot.botDetail.deployments.version', { version: deployment.version || '?' })}
                    </span>
                    {isActive && (
                        <Badge 
                            variant="outline" 
                            style={{
                                backgroundColor: 'var(--deployments-sidebar-active-badge-bg)',
                                color: 'var(--deployments-sidebar-active-badge-text)',
                                borderColor: 'var(--deployments-sidebar-active-badge-text)',
                            }}
                        >
                            {t('chatbot.botDetail.deployments.active')}
                        </Badge>
                    )}
                </div>
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--deployments-sidebar-date-text)',
                }}>
                    {deploymentDate}
                </span>
            </div>
            
            <div className="flex gap-4 mt-2">
                <div style={{
                    fontSize: '0.75rem',
                    color: 'var(--deployments-sidebar-date-text)',
                }}>
                    <span style={{
                        fontWeight: '500',
                        color: 'var(--deployments-sidebar-sessions-text)',
                    }}>
                        {sessionCount}
                    </span> {t('chatbot.botDetail.deployments.sessionsLabel')}
                </div>
                {activeSessions > 0 && (
                    <div style={{
                        fontSize: '0.75rem',
                        color: 'var(--deployments-sidebar-date-text)',
                    }}>
                        <span style={{
                            fontWeight: '500',
                            color: 'var(--deployments-sidebar-active-badge-text)',
                        }}>
                            {activeSessions}
                        </span> {t('chatbot.botDetail.deployments.activeLabel')}
                    </div>
                )}
            </div>
        </div>
    );
});
